import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserProfileId } from '../users';
import type { PushTokenData } from '@/types/notifications';

// Check if users have active push tokens
export async function checkUsersPushTokens(userIds: number[]): Promise<{
  usersWithTokens: number[];
  usersWithoutTokens: number[];
  totalTokens: number;
}> {
  try {
    const { data, error } = await supabase
      .from('user_push_tokens')
      .select('user_id')
      .in('user_id', userIds)
      .eq('is_active', true);

    if (error) throw error;

    const usersWithTokens = [...new Set(data?.map(t => t.user_id) || [])];
    const usersWithoutTokens = userIds.filter(id => !usersWithTokens.includes(id));

    return {
      usersWithTokens,
      usersWithoutTokens,
      totalTokens: data?.length || 0,
    };
  } catch (error) {
    console.error('Error checking user push tokens:', error);
    return {
      usersWithTokens: [],
      usersWithoutTokens: userIds,
      totalTokens: 0,
    };
  }
}

// Add a test push token for current user (for testing purposes)
export async function addTestPushToken(): Promise<{ success: boolean; message: string }> {
  try {
    const currentUserId = await getCurrentUserProfileId();

    const testToken = `fake_fcm_token_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const { error } = await supabase
      .from('user_push_tokens')
      .insert({
        user_id: currentUserId,
        push_token: testToken,
        platform: 'web',
        device_model: 'Test Device',
        app_version: '1.0.0',
        os_version: 'Web',
        is_active: true,
      });

    if (error) throw error;

    return {
      success: true,
      message: `Test push token added for user ${currentUserId}`,
    };
  } catch (error) {
    console.error('Error adding test push token:', error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
}

// Register a push token for a user
export const registerPushToken = async (tokenData: Omit<PushTokenData, 'id'>): Promise<PushTokenData> => {
  try {
    const { data, error } = await supabase
      .from('user_push_tokens')
      .upsert({
        ...tokenData,
        is_active: true,
        last_used_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,push_token'
      })
      .select()
      .single();

    if (error) throw error;
    return data as PushTokenData;
  } catch (error) {
    console.error('Error registering push token:', error);
    throw error;
  }
};

// Get user's push tokens
export const getUserPushTokens = async (userId: number): Promise<PushTokenData[]> => {
  try {
    const { data, error } = await supabase
      .from('user_push_tokens')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true);

    if (error) throw error;
    return (data || []) as PushTokenData[];
  } catch (error) {
    console.error('Error getting user push tokens:', error);
    throw error;
  }
};

// Deactivate a push token
export const deactivatePushToken = async (tokenId: number): Promise<void> => {
  try {
    const { error } = await supabase
      .from('user_push_tokens')
      .update({ is_active: false })
      .eq('id', tokenId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deactivating push token:', error);
    throw error;
  }
};
